// MACHINERY V2 (Stroje) — the MIDAS readiness LEGEND (M2.16c).
//
// Rendered truth: mobile/screenshots/19-katalog-readiness.png. Reference impl:
// mobile/machinery-mobile-katalog.jsx `MbReadinessPhone` — VALUES taken, code
// not.
//
// Three tier cards, each a dot + label + explanatory bullets, answering the
// question the katalog card's readiness row raises: what do these levels MEAN.
//
// ============================================================
//  A SHEET, NOT A ROUTE — DELIBERATE
// ============================================================
// The board draws the legend as a full phone page. It ships as a `SheetV2` off
// the katalog page head (catalog/CatalogMobileHeader.tsx). A new route would
// cost a path, a permission guard, a back-stack entry and a nav decision, and
// buy nothing a sheet does not already give for a help surface — while the sheet
// additionally keeps the user's list, filters and scroll position underneath it,
// which is exactly what someone checking what "Ciastocny profil" means wants to
// come back to.
//
// ============================================================
//  >>> DELIBERATE DEVIATION: THE PER-TIER COUNTS ARE NOT SHIPPED.
// ============================================================
// The board prints "57 modelov / 31 modelov / 36 modelov" beside the three
// labels. THERE IS NO READINESS-TIER BREAKDOWN IN THE CATALOG API.
// `useCatalogBrowse` (catalog/useCatalogBrowse.ts) receives exactly two kinds of
// number: `result.totalCount` for the current query, and per-CATEGORY facet
// counts from `getCatalogCategories`. Neither is a tier count, and the only
// tier-bearing data on the client is the loaded PAGE of models — at most
// `pageSize` rows — so counting them would print "20 modelov" under a label that
// claims to describe the whole catalog. That is the data-honesty failure this
// project has ruled against before, and it would be worst here, in the very
// surface whose job is to explain what the platform knows.
//
// A backend ask is filed. The same missing datum is why
// catalog/CatalogMobileHeader.tsx drops the board's "· 57 OEM ready" meta
// segment — ONE ask covers both. Do not fill either from the loaded page.
//
// >>> THE TIERS THEMSELVES COME FROM readinessTier.ts, NOT FROM HERE. <<< This
// component renders `READINESS_TIERS`; the katalog card renders a descriptor
// from the same table. One derivation, two consumers — so the legend can never
// explain a level the card names differently.
//
// REDUCED MOTION: nothing here animates beyond SheetV2's own entrance keyframe,
// which is correct at its final resting state with the animation never running
// (mobile/machinery-v2-mobile.scss `machinery-v2-sheet-in`).
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import SheetV2 from "../mobile/SheetV2";
import { READINESS_TIERS } from "./readinessTier";

export interface ReadinessLegendSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ReadinessLegendSheet: React.FC<ReadinessLegendSheetProps> = ({
  open,
  onOpenChange,
}) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <SheetV2
      open={open}
      onOpenChange={onOpenChange}
      title={translate("myMachinery:midasReadiness")}
      footer={
        <button
          type="button"
          className="btn"
          onClick={() => onOpenChange(false)}
          style={{
            flex: 1,
            height: 46,
            // >= 44px BOTH axes. flex:1 already clears the width on any phone;
            // the floor is the law, not a prediction.
            minWidth: 44,
            justifyContent: "center",
            borderRadius: 10,
            fontSize: 13.5,
          }}
        >
          {translate("myMachinery:close")}
        </button>
      }
    >
      <div
        style={{
          fontSize: 11.5,
          color: "var(--muted)",
          padding: "0 0 10px",
          lineHeight: 1.45,
        }}
      >
        {translate("myMachinery:v2.catalog.tierIntro")}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 0 }}>
        {READINESS_TIERS.map((tier) => (
          <div
            key={tier.tier}
            style={{
              background: "var(--card-2)",
              border: "1px solid var(--border)",
              borderRadius: 12,
              padding: "12px 14px",
              minWidth: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <span
                aria-hidden
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: 999,
                  background: tier.ink,
                  flexShrink: 0,
                  display: "inline-block",
                }}
              />
              <span
                style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: "var(--ink)",
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {translate(tier.labelKey)}
              </span>
              {/* The board's "57 modelov" sits here. See the header — the datum
                  does not exist and the page count is not a substitute. */}
            </div>

            <div style={{ marginTop: 8, minWidth: 0 }}>
              {tier.bulletKeys.map((key) => (
                <div
                  key={key}
                  style={{
                    display: "flex",
                    gap: 8,
                    padding: "4px 0",
                    fontSize: 12,
                    color: "var(--muted)",
                    lineHeight: 1.45,
                    minWidth: 0,
                  }}
                >
                  <span aria-hidden style={{ color: tier.ink, flexShrink: 0 }}>
                    ·
                  </span>
                  <span style={{ minWidth: 0 }}>{translate(key)}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </SheetV2>
  );
};

export default ReadinessLegendSheet;
